/**
 * MoneyBar — progress bar showing Quote vs WIP vs Money on Account.
 *
 * Props:
 *   quote          Number   The quoted fee (£). 0/null = no quote marker.
 *   wip            Number   Work in progress value (£).
 *   moneyOnAccount Number   Total money received on account (£).
 *   compact        Boolean  Compact mode for Pipeline Mini rows (6px bar, tooltip only).
 */

import { useState, useRef } from "react";
import axios from "axios";
import { API } from "../config/api";

function fmt(n) {
  if (n == null || isNaN(n)) return "£0";
  return "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function MoneyBar({ quote = 0, wip = 0, moneyOnAccount = 0, compact = false, onEditQuote, matterRef, showWhenEmpty = false }) {
  const [hovering, setHovering] = useState(false);
  const [editingQuote, setEditingQuote] = useState(false);
  const [quoteInput, setQuoteInput] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const handleEditQuote = () => {
    setQuoteInput(quote > 0 ? String(quote) : "");
    setEditingQuote(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSaveQuote = async () => {
    const val = parseFloat(quoteInput.replace(/[£,]/g, ""));
    if (isNaN(val) || val < 0) { setEditingQuote(false); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/money-on-account/${matterRef}/quote`, { amount: val });
      if (onEditQuote) onEditQuote(val);
    } catch (err) {
      // PR Q: surface the failure — a silent quote-save fail is
      // worse than no save at all because Vanessa thinks the value
      // was recorded when it was not.
      if (typeof window !== "undefined" && typeof window.errorMessage === "function") {
        try { window.errorMessage(err, `Failed to save quote for ${matterRef}`); } catch (_) { /* ignore */ }
      }
    }
    setSaving(false);
    setEditingQuote(false);
  };

  const q = quote || 0;
  const w = wip || 0;
  const moa = moneyOnAccount || 0;

  // Nothing to show — in compact/pipeline mode, render a placeholder bar
  const isEmpty = q === 0 && w === 0 && moa === 0;
  // BM-H3723: the matter page always shows the bar, so a matter with no
  // quote, WIP or money on account says so instead of the bar vanishing.
  if (isEmpty && !compact && !showWhenEmpty) return null;

  // Scale: max of quote, wip, moa — the bar stretches to this value
  const maxVal = Math.max(q, w, moa) || 1;

  const wipPct = Math.min((w / maxVal) * 100, 100);
  const moaPct = Math.min((moa / maxVal) * 100, 100);
  const quotePct = q > 0 ? Math.min((q / maxVal) * 100, 100) : 0;

  const tooltipText = `Quote: ${fmt(q)} | WIP: ${fmt(w)} | On A/C: ${fmt(moa)}`;

  // ── Compact mode (Pipeline Mini rows) ─────────────────────────────────────
  // PR Q: empty-state placeholder is now visible. Previously this was
  // a 6px bar at 15% opacity which Vanessa correctly described as
  // having "gone" — you could not see it against the row background.
  // Now it is 4px taller, full opacity dashed outline, and labelled
  // so an empty row is clearly distinguishable from a missing component.
  if (compact && isEmpty) {
    return (
      <div
        className="relative w-full h-2.5 bg-slate-800/70 rounded-full overflow-hidden cursor-pointer group border border-dashed border-slate-600/60"
        title="No quote, WIP or money-on-account recorded \u2014 click to set quote"
        onClick={() => matterRef && handleEditQuote()}
        data-testid="money-bar-compact-empty"
      >
        <span className="absolute inset-0 flex items-center justify-center text-[9px] uppercase tracking-wider text-slate-400 font-medium pointer-events-none">
          no money data
        </span>
      </div>
    );
  }
  if (compact) {
    return (
      <div
        className="relative w-full h-1.5 bg-slate-800 rounded-full overflow-hidden group"
        title={tooltipText}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        data-testid="money-bar-compact"
      >
        {/* WIP fill (amber) */}
        <div
          className="absolute inset-y-0 left-0 bg-status-amber rounded-full transition-all duration-300"
          style={{ width: `${wipPct}%` }}
        />
        {/* Money on Account fill (teal, layered on top) */}
        <div
          className="absolute inset-y-0 left-0 bg-teal rounded-full transition-all duration-300"
          style={{ width: `${moaPct}%`, opacity: 0.85 }}
        />
        {/* Quote marker */}
        {quotePct > 0 && quotePct < 100 && (
          <div
            className="absolute inset-y-0 w-px bg-white/70"
            style={{ left: `${quotePct}%` }}
          />
        )}

        {/* Hover tooltip */}
        {hovering && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[9px] text-white shadow-lg z-50 pointer-events-none">
            {tooltipText}
          </div>
        )}
      </div>
    );
  }

  // ── Full mode (with labels) ───────────────────────────────────────────────
  return (
    <div className="w-full" data-testid="money-bar">
      {/* Labels above the bar */}
      <div className="flex items-end justify-between mb-1 text-[10px]">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-status-amber inline-block" />
            <span className="text-status-amber">WIP {fmt(w)}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-teal inline-block" />
            <span className="text-teal">On A/C {fmt(moa)}</span>
          </span>
        </div>
        {q > 0 && !editingQuote && (
          <span className="text-txt-muted flex items-center gap-1">
            Quote {fmt(q)}
            {onEditQuote && matterRef && (
              <button onClick={handleEditQuote} className="text-[10px] opacity-50 hover:opacity-100">✏️</button>
            )}
          </span>
        )}
        {q === 0 && !editingQuote && onEditQuote && matterRef && (
          <button onClick={handleEditQuote} className="text-[10px] text-teal hover:text-teal">
            + Add quote
          </button>
        )}
        {editingQuote && (
          <span className="flex items-center gap-1">
            <span className="text-[10px] text-txt-muted">£</span>
            <input
              ref={inputRef}
              value={quoteInput}
              onChange={(e) => setQuoteInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveQuote(); if (e.key === "Escape") setEditingQuote(false); }}
              onBlur={handleSaveQuote}
              disabled={saving}
              className="w-20 bg-slate-800 border border-slate-600 rounded px-1 py-0.5 text-[10px] text-white outline-none"
              placeholder="0"
            />
          </span>
        )}
      </div>

      {/* Bar */}
      <div className="relative w-full h-3 bg-slate-800 rounded-full overflow-visible">
        {/* WIP fill (amber) — always rendered first (behind) */}
        <div
          className="absolute inset-y-0 left-0 bg-status-amber rounded-full transition-all duration-300"
          style={{ width: `${wipPct}%` }}
        />
        {/* Money on Account fill (teal) — layered on top */}
        <div
          className="absolute inset-y-0 left-0 bg-teal rounded-full transition-all duration-300"
          style={{ width: `${moaPct}%`, opacity: 0.85 }}
        />
        {/* Quote marker (white notch) */}
        {quotePct > 0 && quotePct < 100 && (
          <div
            className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-white rounded-full"
            style={{ left: `${quotePct}%` }}
          />
        )}
      </div>

      {/* Status hint */}
      <div className="mt-1 text-[9px] text-txt-muted">
        {isEmpty ? (
          <span data-testid="money-bar-empty-hint">No quote, WIP or money on account recorded yet</span>
        ) : moa > w ? (
          <span className="text-teal">Client ahead by {fmt(moa - w)}</span>
        ) : w > moa ? (
          <span className="text-status-amber">WIP exceeds payments by {fmt(w - moa)}</span>
        ) : (
          <span className="text-teal">WIP and payments balanced</span>
        )}
      </div>
    </div>
  );
}
